import { ProgrammeCitiesField } from "@/components/ProgrammeCitiesField";
import { LeadBriefAttachments } from "@/components/LeadBriefAttachments";
import { fetchLeadReportStates } from "@/lib/leadReportStates";
import { useState, useEffect } from "react";
import { copyItineraryForLead } from "@/lib/itineraryItemCopy";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StudentIntakeTab } from "@/components/student-intake/StudentIntakeTab";
import { ArrowLeft, Calendar as CalendarIcon, DollarSign, MapPin, Users, XCircle, RotateCcw, AlertCircle, Copy, Mail, Link, ExternalLink, FileText, Eye, GripVertical } from "lucide-react";
import { useDraggablePanel } from "@/hooks/useDraggablePanel";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, ChevronDown } from "lucide-react";
import { Session } from "@supabase/supabase-js";
import { ItineraryBuilder } from "@/components/ItineraryBuilder";
import { ItineraryList } from "@/components/ItineraryList";
import { NormaliseLocationsButton } from "@/components/NormaliseLocationsButton";
import { ProgrammeNotesCard } from "@/components/ProgrammeNotesCard";
import { EditLeadDialog } from "@/components/EditLeadDialog";
import { QuotePreviewDialog } from "@/components/QuotePreviewDialog";
import { QuoteViewer } from "@/components/QuoteViewer";
import { DocumentVault } from "@/components/DocumentVault";
import { LeadClientNotes } from "@/components/LeadClientNotes";
import { StickyNoteBoard } from "@/components/sticky/StickyNoteBoard";
import { CostingsSpreadsheet } from "@/components/CostingsSpreadsheet";
import { SendQuoteButton } from "@/components/SendQuoteButton";
import { UndoQuoteSendButton } from "@/components/UndoQuoteSendButton";
import { QuoteAcceptanceButton } from "@/components/QuoteAcceptanceButton";
import { SaveQuoteRevisionButton } from "@/components/SaveQuoteRevisionButton";
import { UnifiedActivityFeed } from "@/components/UnifiedActivityFeed";
import { CreateInvoicesButton } from "@/components/CreateInvoicesButton";
import { InvoiceManagement } from "@/components/InvoiceManagement";
import { TopUpInvoicePanel } from "@/components/TopUpInvoicePanel";
import { useUserRole } from "@/hooks/useUserRole";
import { ChangeReasonDialog } from "@/components/ChangeReasonDialog";
import { CancelLeadDialog } from "@/components/CancelLeadDialog";
import { CreateAmendmentDialog } from "@/components/CreateAmendmentDialog";
import { SupplierQuotesTab } from "@/components/SupplierQuotesTab";
import { BudgetActualTab } from "@/components/BudgetActualTab";
import { RequestSupplierQuoteDialog } from "@/components/RequestSupplierQuoteDialog";
import { MessageSquare } from "lucide-react";
import { EnhancedCommentsPanel } from "@/components/EnhancedCommentsPanel";
import { LeadEmailThread } from "@/components/LeadEmailThread";
import { LeadEmailAddress } from "@/components/LeadEmailAddress";
import { useLeadEmails } from "@/hooks/useLeadEmails";
import { calculateItemTotalCost } from "@/lib/itineraryCostUtils";
import { baseItemCost } from "@/lib/quoteChoices";
import { useParticipantOptions } from "@/hooks/useParticipantOptions";
import { GenerateProgrammeDialog } from "@/components/GenerateProgrammeDialog";
import { ItineraryPreviewDialog } from "@/components/ItineraryPreviewDialog";
import { ProgrammeViewer } from "@/components/ProgrammeViewer";
import { DeleteLeadDialog } from "@/components/DeleteLeadDialog";
import { ViewClientDialog } from "@/components/ViewClientDialog";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SubmitReportDialog } from "@/components/SubmitReportDialog";
import { ViewReportDialog } from "@/components/ViewReportDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle } from "lucide-react";

const STATUSES = [
  { value: "lead", label: "Lead" },
  { value: "quote_generated", label: "Quote Generated" },
  { value: "quote_sent", label: "Quote Sent" },
  { value: "quote_accepted", label: "Quote Accepted" },
  { value: "amendment_in_progress", label: "Amendment in Progress" },
  { value: "invoicing", label: "Invoicing" },
  { value: "executing", label: "Execution" },
  { value: "reporting", label: "Reporting" },
  { value: "completed", label: "Completion" },
  { value: "cancelled", label: "Cancelled" },
  { value: "aborted", label: "Aborted" },
];

function ParticipantOptionsSummaryInline({ leadId, fallbackPax }: { leadId: string; fallbackPax: number | null }) {
  const { options, isLoading } = useParticipantOptions(leadId);

  if (isLoading || options.length === 0) {
    return (
      <div>
        <p className="text-muted-foreground">Travelers</p>
        <p className="font-medium">{fallbackPax ?? 1} pax</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-muted-foreground">Participant Options</p>
      <div className="space-y-0.5">
        {options.map((opt) => (
          <p key={opt.id} className="font-medium text-sm">
            {opt.label}: {opt.quantity} pax
          </p>
        ))}
      </div>
    </div>
  );
}

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: roles, isLoading: isLoadingRoles } = useUserRole();
  const isAdmin = roles?.includes("admin") || false;
  const [session, setSession] = useState<Session | null>(null);
  const [showReverseDialog, setShowReverseDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [showCancelReasonDialog, setShowCancelReasonDialog] = useState(false);
  const [showAmendmentDialog, setShowAmendmentDialog] = useState(false);
  const [showSubmitReport, setShowSubmitReport] = useState(false);
  const [showViewReport, setShowViewReport] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
  const [showFullItineraryQuoteDialog, setShowFullItineraryQuoteDialog] = useState(false);
  const [showItineraryPreview, setShowItineraryPreview] = useState(false);
  const [showGenerateProgramme, setShowGenerateProgramme] = useState(false);
  // showProgrammeDialog removed — now in QuoteViewer
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  // Tab navigation from URL params (for notification deep links)
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "itinerary";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [quotesOpen, setQuotesOpen] = useState(false);
  const [isLg, setIsLg] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsLg(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const fromNotification = searchParams.get("tab") === "communications";

  // Draggable floating itinerary toolbar
  const {
    containerRef: toolbarRef,
    position: toolbarPos,
    isCustomPosition: toolbarMoved,
    isDragging: toolbarDragging,
    handleDragStart: startToolbarDrag,
    reset: resetToolbarPos,
  } = useDraggablePanel("itinerary-toolbar-position");
  
  // Sync activeTab when URL params change (for notifications when already on page).
  // Depend on the primitive tab value rather than the unstable searchParams
  // object identity to avoid a render loop.
  const tabParam = searchParams.get("tab");
  useEffect(() => {
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);
  
  // Scroll to discussion section when arriving from a notification
  useEffect(() => {
    if (activeTab === "communications") {
      const timer = setTimeout(() => {
        const discussionSection = document.getElementById("team-discussion");
        if (discussionSection) {
          discussionSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [activeTab]);
  
  // Get lead emails for unread count in tab
  const { unreadCount: emailUnreadCount } = useLeadEmails(id || "");

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
  }, []);

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("*, profiles!left(full_name, email), clients!leads_client_id_fkey(id, name, email, phone, company, country, notes, created_at, access_token)")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { data: leadReportState } = useQuery({
    queryKey: ["lead-wrapup-report", id],
    queryFn: async () => (await fetchLeadReportStates([id as string]))[0] ?? null,
    enabled: !!id,
  });

  const { data: itineraryItems } = useQuery({
    queryKey: ["itinerary-items", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("itinerary_items")
        .select(`
          *,
          linked_product:product_id (
            id,
            product_name,
            hero_image_url
          )
        `)
        .eq("lead_id", id)
        .order("date", { ascending: true, nullsFirst: false })
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data;
    },
  });


  // Query for quotations to check for accepted quote fallback (includes legacy quotes where document_type is null)
  const { data: quotations } = useQuery({
    queryKey: ["quotations-status-check", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotations")
        .select("id, status, document_type")
        .eq("lead_id", id)
        .is("archived_at", null)
        .or("document_type.eq.quote,document_type.is.null");

      if (error) throw error;
      return data;
    },
  });

  // Query for the lead's calendar event
  const { data: calendarEvent } = useQuery({
    queryKey: ["lead-calendar-event", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("calendar_events")
        .select("id")
        .eq("related_entity_id", id)
        .eq("related_entity_type", "lead")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ newStatus, reason }: { newStatus: string; reason: string }) => {
      const { error } = await supabase
        .from("leads")
        .update({ status: newStatus as any })
        .eq("id", id);

      if (error) throw error;

      // Rolling back out of execution: return linked bookings to awaiting-payment
      if (lead?.status === "executing" && ["invoicing", "quote_accepted", "quote_sent"].includes(newStatus)) {
        await supabase
          .from("coffeebeans_bookings")
          .update({ payment_status: "invoice_pending" })
          .eq("lead_id", id)
          .eq("payment_status", "paid");
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("activity_log").insert([
          {
            lead_id: id,
            user_id: user.id,
            action: "Status Changed",
            details: `Status updated to ${newStatus}. Reason: ${reason}`,
          },
        ]);
        
        await supabase.from("lead_changes").insert([
          {
            lead_id: id,
            user_id: user.id,
            field_changed: "status",
            old_value: lead?.status || "",
            new_value: newStatus,
            change_source: reason,
          },
        ]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", id] });
      queryClient.invalidateQueries({ queryKey: ["unified-activity-feed", id] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Status updated and audit log recorded");
    },
  });

  const handleStatusChangeRequest = (newStatus: string) => {
    setPendingStatusChange(newStatus);
    setShowReasonDialog(true);
  };

  const handleConfirmStatusChange = async (reason: string) => {
    if (pendingStatusChange) {
      await updateStatusMutation.mutateAsync({ 
        newStatus: pendingStatusChange, 
        reason 
      });
      setPendingStatusChange(null);
      setShowReasonDialog(false);
    }
  };



  const createAmendmentMutation = useMutation({
    mutationFn: async (reason: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get current lead version
      const currentVersion = lead?.version || 1;
      const newVersion = currentVersion + 1;

      // Update lead status and increment version
      const { error: updateError } = await supabase
        .from("leads")
        .update({ 
          status: "amendment_in_progress",
          version: newVersion
        })
        .eq("id", id);

      if (updateError) throw updateError;

      // Log the amendment creation
      await supabase.from("activity_log").insert({
        lead_id: id,
        user_id: user.id,
        action: "Amendment Started",
        details: `Amendment ${newVersion} started. Reason: ${reason}`,
      });

      await supabase.from("lead_changes").insert({
        lead_id: id,
        user_id: user.id,
        field_changed: "status",
        old_value: "quote_accepted",
        new_value: "amendment_in_progress",
        change_source: `Amendment: ${reason}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", id] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["unified-activity-feed", id] });
      toast.success("Amendment created - itinerary unlocked for editing");
      setShowAmendmentDialog(false);
    },
    onError: (error: any) => {
      toast.error(`Failed to create amendment: ${error.message}`);
    },
  });

  const duplicateLeadMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const duplicateData = {
        title: `Copy of ${lead.title}`,
        status: 'lead' as const,
        version: 1,
        user_id: user.id,
        brand_id: lead.brand_id,
        brand: lead.brand,
        client_name: lead.client_name,
        client_liaison: lead.client_liaison,
        client_email: lead.client_email,
        client_id: lead.client_id,
        group_reference: lead.group_reference,
        group_description: lead.group_description,
        pax_count: lead.pax_count,
        destination: lead.destination,
        countries: lead.countries,
        is_multi_country: lead.is_multi_country,
        start_date: lead.start_date,
        end_date: lead.end_date,
        currency: lead.currency,
        payment_terms: lead.payment_terms,
        budget_range: lead.budget_range,
        notes: lead.notes,
        lead_received_date: format(new Date(), 'yyyy-MM-dd'),
      };

      const { data: newLead, error } = await supabase
        .from("leads")
        .insert([duplicateData])
        .select()
        .single();

      if (error) throw error;

      // Fetch and duplicate all itinerary items
      const { data: originalItems } = await supabase
        .from("itinerary_items")
        .select("*")
        .eq("lead_id", lead.id);

      let itemCount = 0;
      if (originalItems && originalItems.length > 0) {
        const { mains, alternatives } = copyItineraryForLead(originalItems as any[], {
          lead_id: newLead.id,
          user_id: user.id,
        });
        const duplicatedItems = [...mains, ...alternatives];

        let { error: itemsError } = await supabase
          .from("itinerary_items")
          .insert(mains as any);
        if (!itemsError && alternatives.length > 0) {
          ({ error: itemsError } = await supabase
            .from("itinerary_items")
            .insert(alternatives as any));
        }

        if (itemsError) {
          console.error("Failed to duplicate itinerary items:", itemsError);
        } else {
          itemCount = duplicatedItems.length;
        }
      }

      await supabase.from("activity_log").insert([{
        lead_id: lead.id,
        user_id: user.id,
        action: "Lead Duplicated",
        details: `Lead duplicated to create new lead: ${newLead.id.slice(0, 8)}${itemCount > 0 ? ` with ${itemCount} itinerary items` : ''}`,
      }]);

      return newLead;
    },
    onSuccess: (newLead) => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Lead duplicated successfully!");
      navigate(`/lead/${newLead.id}`);
    },
    onError: (error: any) => {
      toast.error(`Failed to duplicate lead: ${error.message}`);
    },
  });

  const cancelLeadMutation = useMutation({
    mutationFn: async (reason: string) => {
      const { error } = await supabase
        .from("leads")
        .update({ status: "cancelled" })
        .eq("id", id);

      if (error) throw error;

      if (session?.user) {
        await supabase.from("activity_log").insert({
          lead_id: id,
          user_id: session.user.id,
          action: "Lead Cancelled",
          details: `Lead was cancelled. Reason: ${reason}`,
        });

        await supabase.from("lead_changes").insert({
          lead_id: id,
          user_id: session.user.id,
          field_changed: "status",
          old_value: lead?.status || "",
          new_value: "cancelled",
          change_source: reason,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", id] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["unified-activity-feed", id] });
      toast.success("Lead cancelled and audit log recorded");
      setShowCancelDialog(false);
    },
    onError: (error: any) => {
      toast.error(`Failed to cancel lead: ${error.message}`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!lead) {
    return <div>Lead not found</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <Button variant="ghost" onClick={() => navigate("/department/sales")} className="gap-2 mb-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Pipeline
          </Button>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <div className="min-w-0">
              <h1 className={`text-xl sm:text-2xl font-bold text-foreground break-words ${lead.status === 'aborted' ? 'line-through opacity-50' : ''}`}>
                {lead.title}
              </h1>
              <p className="text-muted-foreground mt-1">
                Lead ID: {lead.id.slice(0, 8)}
                {(lead as any).group_reference && (
                  <span> <span className="mx-1">|</span> Reference: <span className="text-foreground font-medium">{(lead as any).group_reference}</span></span>
                )}
              </p>
              {lead.inbound_email_id && (
                <LeadEmailAddress 
                  inboundEmailId={lead.inbound_email_id} 
                  className="mt-2"
                />
              )}
              {lead.profiles && (
                <p className="text-sm text-muted-foreground mt-1">
                  Created by: {
                    Array.isArray(lead.profiles) 
                      ? (lead.profiles[0]?.full_name || lead.profiles[0]?.email || 'Unknown')
                      : (lead.profiles.full_name || lead.profiles.email || 'Unknown')
                  }
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {calendarEvent && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/calendar?eventId=${calendarEvent.id}`)}
                  className="gap-2"
                >
                  <CalendarIcon className="h-4 w-4" />
                  View in Calendar
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => duplicateLeadMutation.mutate()}
                disabled={duplicateLeadMutation.isPending}
                className="gap-2"
              >
                <Copy className="h-4 w-4" />
                {duplicateLeadMutation.isPending ? "Duplicating..." : "Duplicate Lead"}
              </Button>
              
              {lead.status === 'quote_accepted' && (
                <Button
                  onClick={() => setShowAmendmentDialog(true)}
                  className="gap-2"
                  variant="default"
                >
                  Create Amendment
                </Button>
              )}

              {lead.status === 'reporting' && (
                <Button onClick={() => setShowSubmitReport(true)} className="gap-2">
                  <FileText className="h-4 w-4" />
                  Submit Report
                </Button>
              )}
              {leadReportState?.has_any_report && (
                <Button variant="secondary" onClick={() => setShowViewReport(true)} className="gap-2">
                  <Eye className="h-4 w-4" />
                  View Report
                </Button>
              )}

              {/* Generate Programme button moved to QuoteViewer per-quote cards */}
              
              {isAdmin && lead.status === 'aborted' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowReverseDialog(true)}
                  className="gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reverse Abort
                </Button>
              )}
              
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Status</span>
                {isAdmin ? (
                  <Select 
                    value={lead.status} 
                    onValueChange={handleStatusChangeRequest}
                  >
                    <SelectTrigger className="w-[180px] h-8">
                      <SelectValue>
                        {STATUSES.find(s => s.value === lead.status)?.label || lead.status}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((status) => (
                        <SelectItem 
                          key={status.value} 
                          value={status.value}
                          disabled={status.value === lead.status}
                        >
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline" className="w-fit">
                    {STATUSES.find(s => s.value === lead.status)?.label || lead.status}
                  </Badge>
                )}
                {lead.version > 1 && (
                  <p className="text-xs text-muted-foreground">
                    Version {lead.version}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {isAdmin 
                    ? "Admins can manually change status"
                    : "Status updates automatically"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-3 sm:px-4 pt-4 pb-24 sm:pt-8 lg:pb-8 overflow-x-hidden">
        {lead.status === 'quote_accepted' && (
          <Alert className="mb-6 border-amber-500/50 bg-amber-500/10">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <AlertTitle className="text-amber-500">Quote Accepted - Locked</AlertTitle>
            <AlertDescription>
              This itinerary is locked because the quote has been accepted. To make changes, click "Create Amendment" in the header.
            </AlertDescription>
          </Alert>
        )}
        
        {lead.status === 'amendment_in_progress' && (
          <Alert className="mb-6 border-blue-500/50 bg-blue-500/10">
            <AlertCircle className="h-4 w-4 text-blue-500" />
            <AlertTitle className="text-blue-500">Amendment in Progress</AlertTitle>
            <AlertDescription>
              The itinerary is unlocked for editing. Generate a new quote when changes are complete to create Amendment {lead.version > 1 ? lead.version : 1}.
            </AlertDescription>
          </Alert>
        )}
        
        {lead.status === 'cancelled' && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Lead Cancelled - Read Only Mode</AlertTitle>
            <AlertDescription>
              This lead has been cancelled and cannot be edited. To make changes, change the lead status to reactivate it.
            </AlertDescription>
          </Alert>
        )}

        {lead.status === 'completed' && (
          <Alert className="mb-6 border-green-500/50 bg-green-500/10">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <AlertTitle className="text-green-500">Lead Completed</AlertTitle>
            <AlertDescription>
              This lead has been completed and is in read-only mode. 
              To create a new booking for this client, use "Duplicate Lead".
            </AlertDescription>
          </Alert>
        )}


        <div className="mb-6">
          <StickyNoteBoard
            leadId={id!}
            section="overview"
            label="Sticky notes on this lead"
            readOnly={lead.status === 'cancelled'}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Tabbed Interface for Itinerary, Costings, Documents */}
            <Card className="p-3 sm:p-6">
              <Tabs 
                value={activeTab} 
                onValueChange={(value) => {
                  setActiveTab(value);
                  if (value !== "itinerary") {
                    setSearchParams({ tab: value });
                  } else {
                    setSearchParams({});
                  }
                }}
                className="w-full"
              >
                {/* Small screens: section dropdown */}
                <div className="lg:hidden mb-4 flex gap-2">
                  <Select
                    value={activeTab}
                    onValueChange={(value) => {
                      setActiveTab(value);
                      if (value !== "itinerary") setSearchParams({ tab: value });
                      else setSearchParams({});
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Section" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="itinerary">Itinerary</SelectItem>
                      <SelectItem value="costings">Costings & Calcs</SelectItem>
                      <SelectItem value="quotes">Supplier Quotes</SelectItem>
                      {(lead.status === 'executing' || lead.status === 'completed') && (
                        <SelectItem value="budget-actual">Budget vs Actual</SelectItem>
                      )}
                      <SelectItem value="documents">Documents</SelectItem>
                      <SelectItem value="client-notes">Client Notes</SelectItem>
                      <SelectItem value="student-intake">Client Intake</SelectItem>
                      <SelectItem value="communications">
                        Communications{emailUnreadCount > 0 ? ` (${emailUnreadCount})` : ""}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <TabsList className="hidden lg:flex w-full overflow-x-auto scrollbar-hide mb-4">
                  <TabsTrigger value="itinerary" className="flex-shrink-0">Itinerary</TabsTrigger>
                  <TabsTrigger value="costings" className="flex-shrink-0">Costings & Calcs</TabsTrigger>
                  <TabsTrigger value="quotes" className="flex-shrink-0">Supplier Quotes</TabsTrigger>
                  {(lead.status === 'executing' || lead.status === 'completed') && (
                    <TabsTrigger value="budget-actual" className="flex-shrink-0">Budget vs Actual</TabsTrigger>
                  )}
                  <TabsTrigger value="documents" className="flex-shrink-0">Documents</TabsTrigger>
                  <TabsTrigger value="client-notes" className="flex-shrink-0">Client Notes</TabsTrigger>
                  <TabsTrigger value="student-intake" className="flex-shrink-0">Client Intake</TabsTrigger>
                  <TabsTrigger value="communications" className="flex-shrink-0 gap-1">
                    <MessageSquare className="h-4 w-4" />
                    Communications
                    {emailUnreadCount > 0 && (
                      <Badge variant="secondary" className="ml-1 text-xs h-5 min-w-5 px-1">
                        {emailUnreadCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="itinerary" className="space-y-4">
                  <StickyNoteBoard
                    leadId={id!}
                    section="itinerary"
                    label="Sticky notes on the itinerary"
                    readOnly={lead.status === 'cancelled'}
                  />
                  <ProgrammeNotesCard
                    leadId={id!}
                    initialNotes={(lead as any).programme_notes}
                    readOnly={lead.status === 'cancelled' || lead.status === 'quote_accepted'}
                  />
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Itinerary</h2>
                    <p className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                      Building for all options — select option when generating quote
                    </p>
                  </div>
                  {itineraryItems && (
                    <ItineraryList 
                      items={itineraryItems} 
                      leadId={id!} 
                      currency={lead.currency || "ZAR"}
                      paxCount={lead.pax_count || 1}
                      leadStatus={lead.status}
                      leadStartDate={lead.start_date}
                    />
                  )}
                  
                  {/* Quote Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t">
                    <SendQuoteButton 
                      leadId={id!} 
                      currentStatus={lead.status}
                      clientEmail={lead.client_email}
                      clientName={lead.client_name}
                      leadInboundEmailId={lead.inbound_email_id}
                    />
                    <UndoQuoteSendButton leadId={id!} currentStatus={lead.status} />
                    <QuoteAcceptanceButton leadId={id!} currentStatus={lead.status} />
                    <CreateInvoicesButton 
                      leadId={id!} 
                      paymentTerms={lead.payment_terms}
                      quotedPrice={lead.quoted_price}
                      currentStatus={lead.status}
                    />
                    <SaveQuoteRevisionButton leadId={id!} leadStatus={lead.status} />
                    {lead.status === "quote_accepted" && itineraryItems && itineraryItems.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          Quote accepted. Edit the itinerary and click "Save Revision" to track changes.
                        </span>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="costings" className="space-y-4">
                  <StickyNoteBoard
                    leadId={id!}
                    section="costings"
                    label="Sticky notes on costings"
                    readOnly={lead.status === 'cancelled'}
                  />
                  <CostingsSpreadsheet leadId={id!} leadStartDate={lead?.start_date} session={session} paxCount={lead?.pax_count || 1} />
                </TabsContent>

                <TabsContent value="quotes" className="space-y-4">
                  <StickyNoteBoard
                    leadId={id!}
                    section="supplier-quotes"
                    label="Sticky notes on supplier quotes"
                    readOnly={lead.status === 'cancelled'}
                  />
                  <SupplierQuotesTab leadId={id!} />
                </TabsContent>

                {(lead.status === 'executing' || lead.status === 'completed') && (
                  <TabsContent value="budget-actual">
                    <BudgetActualTab leadId={id!} leadCurrency={lead.currency || "ZAR"} />
                  </TabsContent>
                )}

                <TabsContent value="documents" className="space-y-4">
                  <StickyNoteBoard
                    leadId={id!}
                    section="documents"
                    label="Sticky notes on documents"
                    readOnly={lead.status === 'cancelled'}
                  />
                  <DocumentVault leadId={id!} />
                </TabsContent>

                <TabsContent value="client-notes" className="space-y-4">
                  <StickyNoteBoard
                    leadId={id!}
                    section="client-notes"
                    label="Sticky notes on client notes"
                    readOnly={lead.status === 'cancelled'}
                  />
                  <LeadClientNotes leadId={id!} />
                </TabsContent>

                <TabsContent value="student-intake">
                  <StudentIntakeTab leadId={id!} leadTitle={lead.title} />
                </TabsContent>

                <TabsContent value="communications" className="min-h-[400px]">
                  <div className="space-y-6">
                    <StickyNoteBoard
                      leadId={id!}
                      section="communications"
                      label="Sticky notes on communications"
                      readOnly={lead.status === 'cancelled'}
                    />
                    {/* Emails Section */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Client Emails
                      </h3>
                      <LeadEmailThread 
                        leadId={id!} 
                        inboundEmailId={lead.inbound_email_id} 
                        clientEmail={lead.client_email || undefined}
                      />
                    </div>
                    
                    <Separator />
                    
                    {/* Team Discussion Section */}
                    <div id="team-discussion">
                      <EnhancedCommentsPanel 
                        entityId={id!} 
                        entityType="lead" 
                        title="Team Discussion"
                        className="min-h-[400px] max-h-[800px] border rounded-lg"
                        autoExpandAll={fromNotification}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              
              <RequestSupplierQuoteDialog
                open={showFullItineraryQuoteDialog}
                onOpenChange={setShowFullItineraryQuoteDialog}
                mode="full_itinerary"
                leadId={id!}
              />
              {itineraryItems && (
                <ItineraryPreviewDialog
                  open={showItineraryPreview}
                  onOpenChange={setShowItineraryPreview}
                  itineraryItems={itineraryItems}
                  lead={lead}
                  totalCost={itineraryItems
                    .filter(item => item.include_in_quote !== false)
                    .reduce((sum, item) => sum + baseItemCost(item), 0)}
                  currency={lead.currency || "ZAR"}
                  onConfirmGenerate={() => {
                    // Programmatically click the QuotePreviewDialog trigger
                    const quoteBtn = document.querySelector('[data-quote-preview-trigger]') as HTMLButtonElement;
                    if (quoteBtn) quoteBtn.click();
                  }}
                />
              )}
            </Card>

            {/* Invoice Management */}
            {(lead.status === "invoicing" || lead.status === "executing") && (
              <>
                <TopUpInvoicePanel
                  leadId={id!}
                  acceptedQuotationId={lead.accepted_quotation_id ?? null}
                  currency={lead.currency || "ZAR"}
                />
                <InvoiceManagement 
                  leadId={id!} 
                  currency={lead.currency || "ZAR"}
                  currentStatus={lead.status}
                />
              </>
            )}


            {/* Unified Activity Feed (activity log + change history) */}
            <UnifiedActivityFeed leadId={id!} />
          </div>

          {/* Lead Details - Right Column (Sticky) */}
          <div id="lead-sidebar" className="min-w-0 order-first lg:order-none">
            <div className="space-y-3 lg:space-y-6">
              <Card className="p-4 sm:p-6">
                <div className={cn("flex items-center justify-between gap-2", detailsOpen ? "mb-4" : "mb-0 lg:mb-4")}>
                  <button
                    type="button"
                    onClick={() => setDetailsOpen((o) => !o)}
                    className="flex items-center gap-2 text-left min-w-0 lg:pointer-events-none"
                    aria-expanded={detailsOpen}
                  >
                    <h2 className="text-base sm:text-lg font-semibold">Lead Details</h2>
                    <ChevronDown className={cn("h-5 w-5 lg:hidden transition-transform", detailsOpen && "rotate-180")} />
                  </button>
                  <div className="flex gap-2 shrink-0">
                    {lead.status !== 'cancelled' && lead.status !== 'aborted' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowCancelDialog(true)}
                        className="gap-2"
                      >
                        <XCircle className="h-4 w-4" />
                        <span className="hidden sm:inline">Cancel</span>
                      </Button>
                    )}
                    {lead.status !== 'cancelled' && <EditLeadDialog lead={lead} />}
                  </div>
                </div>
                {!detailsOpen && (
                  <div className="lg:hidden mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {lead.client_name && <span>{lead.client_name}</span>}
                    {lead.destination && <span>{lead.destination}</span>}
                    <span>{lead.pax_count} pax</span>
                    {lead.start_date && <span>{format(new Date(lead.start_date), "d MMM yyyy")}</span>}
                  </div>
                )}
                <div className={cn(!detailsOpen && "hidden lg:block")}>
                <div className="grid grid-cols-1 gap-3 text-sm">
                  {lead.profiles && (
                    <div>
                      <p className="text-muted-foreground">Created By</p>
                      <p className="font-medium">
                        {Array.isArray(lead.profiles) 
                          ? (lead.profiles[0]?.full_name || lead.profiles[0]?.email || 'Unknown')
                          : (lead.profiles.full_name || lead.profiles.email || 'Unknown')}
                      </p>
                    </div>
                  )}
                  {lead.client_name && (
                    <div>
                      <p className="text-muted-foreground">Client</p>
                      {lead.client_id ? (
                        <button
                          onClick={() => setShowClientDialog(true)}
                          className="font-medium text-primary hover:underline cursor-pointer text-left"
                        >
                          {lead.client_name}
                        </button>
                      ) : (
                        <p className="font-medium">{lead.client_name}</p>
                      )}
                    </div>
                  )}

                  {(() => {
                    const leadClient = Array.isArray(lead.clients) ? lead.clients[0] : lead.clients;
                    if (!leadClient?.access_token) return null;
                    return (
                    <div>
                      <p className="text-muted-foreground">Client Portal</p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const portalUrl = `${window.location.origin}/portal/client/${leadClient.access_token}`;
                            navigator.clipboard.writeText(portalUrl);
                            toast.success("Client portal link copied!");
                          }}
                          className="h-7 gap-1.5 text-xs"
                        >
                          <Link className="h-3 w-3" />
                          Copy
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            window.open(`/portal/client/${leadClient.access_token}`, '_blank');
                          }}
                          className="h-7 gap-1.5 text-xs"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Open
                        </Button>
                      </div>
                    </div>
                    );
                  })()}

                  {lead.client_liaison && (
                    <div>
                      <p className="text-muted-foreground">Client Liaison</p>
                      <p className="font-medium">{lead.client_liaison}</p>
                    </div>
                  )}

                  {lead.group_reference && (
                    <div>
                      <p className="text-muted-foreground">Group Reference</p>
                      <p className="font-medium">{lead.group_reference}</p>
                    </div>
                  )}

                  <ParticipantOptionsSummaryInline leadId={lead.id} fallbackPax={lead.pax_count} />

                  {lead.destination && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-3.5 w-3.5 text-primary mt-0.5" />
                      <div>
                        <p className="text-muted-foreground">Destination</p>
                        <p className="font-medium">{lead.destination}</p>
                      </div>
                    </div>
                  )}

                  <ProgrammeCitiesField leadId={lead.id} value={(lead as any).programme_cities ?? null} />

                  {lead.lead_received_date && (
                    <div className="flex items-start gap-2">
                      <CalendarIcon className="h-3.5 w-3.5 text-primary mt-0.5" />
                      <div>
                        <p className="text-muted-foreground">Lead Received</p>
                        <p className="font-medium">{format(new Date(lead.lead_received_date), "d MMM yyyy")}</p>
                      </div>
                    </div>
                  )}

                  {lead.start_date && (
                    <div className="flex items-start gap-2">
                      <CalendarIcon className="h-3.5 w-3.5 text-primary mt-0.5" />
                      <div>
                        <p className="text-muted-foreground">Start</p>
                        <p className="font-medium">{format(new Date(lead.start_date), "d MMM yyyy")}</p>
                      </div>
                    </div>
                  )}

                  {lead.end_date && (
                    <div className="flex items-start gap-2">
                      <CalendarIcon className="h-3.5 w-3.5 text-primary mt-0.5" />
                      <div>
                        <p className="text-muted-foreground">End</p>
                        <p className="font-medium">{format(new Date(lead.end_date), "d MMM yyyy")}</p>
                      </div>
                    </div>
                  )}

                  {lead.currency && (
                    <div className="flex items-start gap-2">
                      <DollarSign className="h-3.5 w-3.5 text-primary mt-0.5" />
                      <div>
                        <p className="text-muted-foreground">Currency</p>
                        <p className="font-medium">{lead.currency}</p>
                      </div>
                    </div>
                  )}

                  {lead.budget_range && (
                    <div>
                      <p className="text-muted-foreground">Budget Range</p>
                      <p className="font-medium">{lead.budget_range}</p>
                    </div>
                  )}

                  {lead.quoted_price && (
                    <div className="flex items-start gap-2">
                      <DollarSign className="h-3.5 w-3.5 text-accent mt-0.5" />
                      <div>
                        <p className="text-muted-foreground">Quoted Price</p>
                        <p className="font-medium text-base">
                          {lead.currency} {lead.quoted_price.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <Separator className="my-3" />
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Group Description</p>
                  {lead.group_description ? (
                    <p className="text-sm text-foreground whitespace-pre-wrap">{lead.group_description}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No description</p>
                  )}
                  <p className="text-muted-foreground text-xs mt-3">Attachments</p>
                  <LeadBriefAttachments leadId={lead.id} />
                </div>

                {lead.payment_terms && (
                  <>
                    <Separator className="my-3" />
                    <div>
                      <p className="text-muted-foreground text-sm mb-1">Payment Terms</p>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{lead.payment_terms}</p>
                    </div>
                  </>
                )}

                {lead.notes && (
                  <>
                    <Separator className="my-3" />
                    <div>
                      <p className="text-muted-foreground text-sm mb-1">Notes</p>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{lead.notes}</p>
                    </div>
                  </>
                )}
                </div>
              </Card>

              {/* Generated quotes — collapsible on small screens */}
              <div className="lg:contents">
                <button
                  type="button"
                  onClick={() => setQuotesOpen((o) => !o)}
                  className="lg:hidden w-full flex items-center justify-between rounded-lg border bg-card px-4 py-3 text-left"
                  aria-expanded={quotesOpen}
                >
                  <span className="text-base font-semibold">Generated Quotes & Programmes</span>
                  <ChevronDown className={cn("h-5 w-5 transition-transform", quotesOpen && "rotate-180")} />
                </button>
                <div className={cn("space-y-6 mt-3 lg:mt-0", !quotesOpen && "hidden lg:block")}>
                  <QuoteViewer 
                    leadId={id!} 
                    currency={lead.currency || "ZAR"} 
                    leadStatus={lead.status} 
                    acceptedQuotationId={lead.accepted_quotation_id}
                    lead={lead}
                  />
                  <ProgrammeViewer 
                    leadId={id!}
                    clientEmail={lead.client_email || ""}
                    clientName={lead.client_name || ""}
                    leadInboundEmailId={lead.inbound_email_id}
                  />
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Floating Itinerary Toolbar - only visible on itinerary tab */}
        {activeTab === "itinerary" && (
          <div
            ref={toolbarRef}
            style={toolbarMoved && isLg ? { top: toolbarPos.y, left: toolbarPos.x } : undefined}
            className={cn(
              "fixed z-40 bg-card border shadow-lg flex flex-nowrap items-center gap-2",
              "left-2 right-2 bottom-2 rounded-xl px-2 py-2 overflow-x-auto scrollbar-hide [&>*]:shrink-0",
              "lg:right-auto lg:rounded-full lg:pl-2 lg:pr-4 lg:py-2.5 lg:overflow-visible",
              (!toolbarMoved || !isLg) && "lg:bottom-6 lg:left-1/2 lg:-translate-x-1/2",
              toolbarDragging && "select-none"
            )}
          >
            <div
              onMouseDown={startToolbarDrag}
              onTouchStart={startToolbarDrag}
              onDoubleClick={resetToolbarPos}
              title="Drag to move · double-click to reset"
              className="hidden lg:block cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0 px-1 touch-none"
              aria-label="Move toolbar"
            >
              <GripVertical className="h-4 w-4" />
            </div>
            <ItineraryBuilder 
              leadId={id!} 
              session={session} 
              leadStatus={lead.status}
              leadStartDate={lead.start_date}
              paxCount={lead.pax_count || 1}
              leadCountries={lead.countries}
              leadDestination={lead.destination}
              isMultiCountry={lead.is_multi_country}
            />
            {itineraryItems && itineraryItems.length > 0 && (
              <>
                <Button onClick={() => setShowItineraryPreview(true)} variant="outline" size="sm">
                  <Eye className="h-4 w-4 mr-1.5" />
                  Preview
                </Button>
                <NormaliseLocationsButton
                  leadId={id!}
                  disabled={lead.status === "cancelled" || lead.status === "completed"}
                />
                <Button onClick={() => setShowGenerateProgramme(true)} variant="outline" size="sm">
                  <FileText className="h-4 w-4 mr-1.5" />
                  Generate Programme
                </Button>
                <QuotePreviewDialog 
                  leadId={id!} 
                  totalCost={itineraryItems
                    .filter(item => item.include_in_quote !== false)
                    .reduce((sum, item) => sum + baseItemCost(item), 0)}
                  currency={lead.currency || "ZAR"}
                  paymentTerms={lead.payment_terms}
                  leadStatus={lead.status}
                  lead={lead}
                />
                <GenerateProgrammeDialog
                  open={showGenerateProgramme}
                  onOpenChange={setShowGenerateProgramme}
                  leadId={id!}
                  lead={lead}
                  acceptedQuotationId={lead.accepted_quotation_id ?? null}
                  onProgrammeGenerated={() => {
                    queryClient.invalidateQueries({ queryKey: ["programmes", id] });
                  }}
                />
              </>
            )}
          </div>
        )}

        {/* Danger Zone - Admin Only */}
        {isAdmin && lead.status !== 'aborted' && (
          <Card className="border-destructive/50 bg-destructive/5 p-6 mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-destructive">
                  Danger Zone
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Permanently delete this lead and all associated data.
                  This action cannot be undone.
                </p>
              </div>
              <Button 
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete Lead Permanently
              </Button>
            </div>
          </Card>
        )}
      </main>

      {/* Reverse Abort Dialog */}
      <AlertDialog open={showReverseDialog} onOpenChange={setShowReverseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reverse Abort?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore the lead from aborted status back to lead status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setShowReverseDialog(false);
              handleStatusChangeRequest("lead");
            }}>
              Reverse Abort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Lead Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the lead as cancelled. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowCancelDialog(false);
                setShowCancelReasonDialog(true);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel Lead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Change Reason Dialog */}
      <ChangeReasonDialog
        open={showReasonDialog}
        onOpenChange={(open) => {
          setShowReasonDialog(open);
          if (!open) setPendingStatusChange(null);
        }}
        onConfirm={handleConfirmStatusChange}
        title="Reason for Status Change"
        description={`Please provide a reason for changing the status to "${STATUSES.find(s => s.value === pendingStatusChange)?.label}". This will be recorded in the audit trail.`}
      />

      {/* Cancel Lead Reason Dialog */}
      <CancelLeadDialog
        open={showCancelReasonDialog}
        onOpenChange={setShowCancelReasonDialog}
        onConfirm={(reason) => cancelLeadMutation.mutate(reason)}
      />

      {/* Create Amendment Dialog */}
      <SubmitReportDialog
        open={showSubmitReport}
        onOpenChange={setShowSubmitReport}
        lead={lead as any}
      />
      <ViewReportDialog
        open={showViewReport}
        onOpenChange={setShowViewReport}
        leadId={id!}
        leadTitle={lead.title}
      />
      <CreateAmendmentDialog
        open={showAmendmentDialog}
        onOpenChange={setShowAmendmentDialog}
        onConfirm={(reason) => createAmendmentMutation.mutate(reason)}
        isLoading={createAmendmentMutation.isPending}
      />

      {/* Generate Programme Dialog moved to QuoteViewer */}

      {/* Delete Lead Dialog */}
      <DeleteLeadDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        leadId={id!}
        leadTitle={lead.title}
      />

      {/* Client Dialog */}
      <ErrorBoundary label="LeadDetail:ClientDialog" silent fallback={null}>
        {(() => {
          const leadClient = Array.isArray(lead.clients) ? lead.clients[0] : lead.clients;
          if (!leadClient) return null;
          return (
            <ViewClientDialog
              client={leadClient}
              open={showClientDialog}
              onOpenChange={setShowClientDialog}
            />
          );
        })()}
      </ErrorBoundary>
    </div>
  );
};

export default LeadDetail;
